import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';
import { DEFAULT_API_BASE, DEFAULT_API_KEY, STORAGE_KEYS } from '@/lib/constants';
import { normalizeBase } from '@/lib/api/releases';
import { storage } from '@/lib/utils/storage';

interface ApiBaseContextValue {
  apiBase: string;
  setApiBase: (next: string) => void;
  apiKey: string;
  setApiKey: (next: string) => void;
}

const ApiBaseContext = createContext<ApiBaseContextValue | null>(null);

/** Holds the configurable export-API base URL and API key, persisted to localStorage. */
export function ApiBaseProvider({ children }: { children: ReactNode }) {
  const [apiBase, setApiBaseState] = useState(() =>
    storage.get(STORAGE_KEYS.apiBase, DEFAULT_API_BASE),
  );

  // Falls back to the build-time key so a fresh browser fetches live data on
  // login; a key the user saves in Settings still wins.
  const [apiKey, setApiKeyState] = useState(() =>
    storage.get(STORAGE_KEYS.apiKey, DEFAULT_API_KEY),
  );

  const setApiBase = useCallback((next: string) => {
    const normalized = normalizeBase(next) || DEFAULT_API_BASE;
    storage.set(STORAGE_KEYS.apiBase, normalized);
    setApiBaseState(normalized);
  }, []);

  const setApiKey = useCallback((next: string) => {
    storage.set(STORAGE_KEYS.apiKey, next);
    setApiKeyState(next);
  }, []);

  const value = useMemo(() => ({ apiBase, setApiBase, apiKey, setApiKey }), [apiBase, setApiBase, apiKey, setApiKey]);
  return <ApiBaseContext.Provider value={value}>{children}</ApiBaseContext.Provider>;
}

// eslint-disable-next-line react-refresh/only-export-components
export function useApiBase(): ApiBaseContextValue {
  const ctx = useContext(ApiBaseContext);
  if (!ctx) throw new Error('useApiBase must be used within an ApiBaseProvider');
  return ctx;
}
